import React, { memo, useCallback, useMemo } from 'react';

import cloneDeep from 'lodash/cloneDeep';

import CityElement from '../CityElement';
import TopPanel from '../../utils/TopPanel';
import DragnDropTable from '../../utils/DragnDrop/DragnDropTable';
import '../../../styles/containers/Parties.css';
import '../../../styles/containers/Cities.css';
import Error from '../../stateMessage/Error';
import { isMobileResolution } from '../../../infrastructure/utils/common';
import { City, CityGuide } from '../../../types/City';

import CitiesMobile from './CitiesMobile';

type Props = {
  cities: City[];
  changeOrder(cities: City[], city: City): void;
  isError: boolean;
  guides: CityGuide[];
  handleOnStatusClick(name: string): void;
  handleOnTopClick(name: string): void;
  deleteCity(name: string): void;
  handleOnExploreClick(event: KeyboardEvent, name: string): void;
  handleOnGuideClick(event: KeyboardEvent, name: string): void;
  uploadPhoto(target: EventTarget & HTMLInputElement, name: string): void;
};

export type CityActions = Pick<
  Props,
  | 'handleOnExploreClick'
  | 'handleOnGuideClick'
  | 'handleOnStatusClick'
  | 'handleOnTopClick'
  | 'uploadPhoto'
  | 'deleteCity'
>;

const CityList = memo(
  ({
    cities = [],
    changeOrder,
    isError,
    guides = [],
    handleOnGuideClick,
    handleOnExploreClick,
    handleOnStatusClick,
    handleOnTopClick,
    uploadPhoto,
    deleteCity,
  }: Props) => {
    const onChangeOrder = useCallback(
      (oldIndex: number, newIndex: number) => {
        if (oldIndex === newIndex) return;
        const updatedCities = cloneDeep(cities);
        let step = 100.0;
        const { order } = updatedCities[newIndex];
        if (oldIndex > newIndex) {
          if (newIndex !== 0) {
            const { order: minOrder } = updatedCities[newIndex - 1];
            while (order - step <= minOrder) {
              step /= 10;
            }
          }
          updatedCities[oldIndex].order = order - step;
        }
        if (newIndex !== cities.length - 1) {
          const maxOrder = cities[newIndex + 1].order;
          while (order + step >= maxOrder) {
            step /= 10;
          }
          updatedCities[oldIndex].order = order + step;
        }
        changeOrder([...updatedCities], updatedCities[oldIndex]);
      },
      [changeOrder, cities]
    );

    const cityActions = {
      handleOnTopClick,
      handleOnStatusClick,
      handleOnExploreClick,
      handleOnGuideClick,
      uploadPhoto,
      deleteCity,
    };

    const content = useMemo(() => {
      return cities.map(c => ({
        ...c,
        picture: {
          ...c.picture,
          data: c.picture.data?.startsWith('https://ik.imagekit.io')
            ? `${c.picture.data}?tr=w-600`
            : c.picture.data,
        },
      }));
    }, [cities]);
    return (
      <>
        {isError && <Error text='Error city status update' />}
        <TopPanel>
          <h1 style={{ marginTop: '0px', paddingLeft: '29px' }}>
            <span className='fas fa-city' />
            Cities
          </h1>
        </TopPanel>
        <div className='data-container'>
          {isMobileResolution() ? (
            <CitiesMobile
              cities={cities}
              handleOnExploreClick={handleOnExploreClick}
              handleOnGuideClick={handleOnGuideClick}
            />
          ) : (
            <>
              {cities.length ? (
                <div key='111' className='table-box tab-cont city-height'>
                  <DragnDropTable
                    // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
                    // @ts-ignore
                    content={content}
                    onChange={onChangeOrder}
                    headers={[
                      '',
                      'City',
                      'Display in Marketplace',
                      'Itinerary Templates',
                      'Top Cities',
                      'Explore',
                      'City Guide',
                      "Guides's status",
                      'Actions',
                    ]}
                    guides={guides}
                    customReorder
                    actions={cityActions}
                    Element={CityElement}
                  />
                </div>
              ) : (
                'Cities List is empty'
              )}
            </>
          )}
        </div>
      </>
    );
  }
);
export default CityList;
